import { useEffect, useState } from 'react';
import { Routes, Route, NavLink } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';
import { AppDataProvider } from './store';
import Auth from './components/Auth';
import Dashboard from './components/Dashboard';
import PropertyList from './components/PropertyList';
import PropertyDetail from './components/PropertyDetail';
import ObligationDetail from './components/ObligationDetail';
import ObligationForm from './components/ObligationForm';
import ProjectList from './components/ProjectList';
import ProjectDetail from './components/ProjectDetail';
import ProjectForm from './components/ProjectForm';
import CalendarView from './components/CalendarView';
import SearchView from './components/SearchView';
import ReportsView from './components/ReportsView';
import SettingsView from './components/SettingsView';

export default function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  if (session === undefined) {
    return <div className="loading-screen">Loading…</div>;
  }

  if (!session) {
    return <Auth />;
  }

  return (
    <AppDataProvider>
      <Shell />
    </AppDataProvider>
  );
}

function Shell() {
  async function handleSignOut() {
    await supabase.auth.signOut();
  }

  return (
    <div className="shell">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark">🌲</span>
          Town Farm Hill Portal
        </div>
        <nav className="topnav">
          <NavLink to="/" end>
            Today
          </NavLink>
          <NavLink to="/properties">Properties</NavLink>
          <NavLink to="/projects">Projects</NavLink>
          <NavLink to="/calendar">Calendar</NavLink>
          <NavLink to="/search">Search</NavLink>
          <NavLink to="/reports">Reports</NavLink>
          <NavLink to="/settings">Settings</NavLink>
        </nav>
        <button className="btn-secondary signout" onClick={handleSignOut}>
          Sign out
        </button>
      </header>

      <main className="content">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/properties" element={<PropertyList />} />
          <Route path="/properties/:propertyId" element={<PropertyDetail />} />
          <Route path="/obligations/new" element={<ObligationForm />} />
          <Route path="/obligations/:obligationId" element={<ObligationDetail />} />
          <Route path="/obligations/:obligationId/edit" element={<ObligationForm />} />
          <Route path="/projects" element={<ProjectList />} />
          <Route path="/projects/new" element={<ProjectForm />} />
          <Route path="/projects/:projectId" element={<ProjectDetail />} />
          <Route path="/projects/:projectId/edit" element={<ProjectForm />} />
          <Route path="/calendar" element={<CalendarView />} />
          <Route path="/search" element={<SearchView />} />
          <Route path="/reports" element={<ReportsView />} />
          <Route path="/settings" element={<SettingsView />} />
        </Routes>
      </main>

      <nav className="bottomnav">
        <NavLink to="/" end>
          <span>🏠</span>Today
        </NavLink>
        <NavLink to="/properties">
          <span>🏘️</span>Properties
        </NavLink>
        <NavLink to="/projects">
          <span>🛠️</span>Projects
        </NavLink>
        <NavLink to="/obligations/new" className="bottomnav-add">
          <span>＋</span>Add
        </NavLink>
        <NavLink to="/calendar">
          <span>📅</span>Calendar
        </NavLink>
        <NavLink to="/search">
          <span>🔍</span>Search
        </NavLink>
      </nav>
    </div>
  );
}
